import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import "./Navbar.css";
import RulesModal from "./community/RulesModal";



function Navbar({ user }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);


  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const notificationRef = useRef(null);

  // ============================
  // LOGOUT
  // ============================
  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/auth");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // =============================
  // Show Rules Modal
  //==============================
const shouldShowRules = () => {
  if (!userData?.rulesAcceptedAt) return true;

  const ONE_YEAR = 365 * 24 * 60 * 60 * 1000;

  return Date.now() - userData.rulesAcceptedAt.toMillis() > ONE_YEAR;
};

  // ============================
// COMMUNITY RULES HANDLERS
// ============================
const handleCommunityClick = () => {
  if (!userData) {
    console.log("User data not loaded yet");
    return; // ⛔ prevent premature navigation
  }

  if (shouldShowRules()) {
    setShowRules(true);
  } else {
    navigate("/community");
  }
};

const handleRulesAccept = async () => {
  await updateDoc(doc(db, "users", user.uid), {
    rulesAcceptedAt: serverTimestamp()
  });

  setShowRules(false);
  navigate("/community");
};

// ============================
// NATIVE BRIDGE HANDLERS
// ============================

const handleEnableNotifications = () => {
  if (window.enableIOSPushNotifications) {
    window.enableIOSPushNotifications();
  } else {
    alert("Notifications are available in the mobile app.");
  }
};



useEffect(() => {
  if (!user) return;

  console.log("Navbar user UID:", user.uid);
  console.log("Running notification query for:", user.uid);

  

  const q = query(
      collection(db, "notifications"),
      where("uid", "==", user.uid),
      orderBy("createdAt", "desc") // 🔥 NEWEST FIRST
    );
    

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setNotifications(items);

    const unread = items.reduce((count, item) => {
      return item.read ? count : count + 1;
    }, 0);

    setUnreadCount(unread);
  });

  return () => unsubscribe();
}, [user]);

useEffect(() => {
  if (!user) return;

  const unsub = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
    if (docSnap.exists()) {
      setUserData(docSnap.data());
    }
  });

  return () => unsub();
}, [user]);

useEffect(() => {
  function handleClickOutside(event) {
    if (
      notificationRef.current &&
      !notificationRef.current.contains(event.target)
    ) {
      setShowNotifications(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);



const handleAccountSettings = () => {
  try {

    if (window.webkit?.messageHandlers?.openSettings) {
      window.webkit.messageHandlers.openSettings.postMessage({});
      return;
    }

  } catch (err) {
    console.warn("Native settings handler failed:", err);
  }

  // Reliable fallback
  window.location.href = "/account";
};





return (
  <>
    <div className="navbar-wrapper">
      <div className="navbar-container">

        {/* Logo */}
        <div className="navbar-logo">
          <Link to="/" className="logo-text">
            EmployPilot
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="navbar-links">
          <Link to="/" className="nav-item">Dashboard</Link>
          <Link to="/pricing" className="nav-item">Pricing</Link>
          <Link to="/contact" className="nav-item">Contact</Link>

          <button
            className="nav-item nav-button"
            onClick={handleCommunityClick}
          >
            Community <span className="pro-badge">PRO</span>
          </button>
        </div>

        {/* Right Side Controls */}
        <div className="navbar-controls">

          {user && (
            <>
              {/* Notifications */}
              <div className="notification-wrapper" ref={notificationRef}>
                <button
                  className="notification-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNotifications(!showNotifications);
                  }}
                >
                  🔔
                  {unreadCount > 0 && (
                    <span className="notif-badge">{unreadCount}</span>
                  )}
                </button>

                {showNotifications && (
                  <div className="notification-dropdown">

                    {notifications.length === 0 ? (
                      <div className="notif-empty">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`notif-item ${!n.read ? "unread" : ""}`}
                          onClick={async () => {

                            await updateDoc(doc(db, "notifications", n.id), {
                              read: true
                            });

                            if (n.link) navigate(n.link);

                            setShowNotifications(false);

                          }}
                        >
                          <strong>{n.title}</strong>
                          <p>{n.body}</p>
                        </div>
                      ))
                    )}

                  </div>
                )}
              </div>

              {/* Account */}
              <button
                className="nav-item nav-button"
                onClick={handleAccountSettings}
              >
                ⚙️ Account
              </button>

              {/* Logout */}
              <button
                className="nav-logout"
                onClick={handleLogout}
              >
                Logout
              </button>
            </>
          )}

          {!user && (
            <Link to="/auth" className="nav-item">Login</Link>
          )}


          {/* Hamburger */}
          <div
            className="hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>

        </div>
      </div>

      {/* Mobile Drawer */}
      {menuOpen && (
        <div className="mobile-menu">

          <Link
            to="/"
            className="mobile-item"
            onClick={() => setMenuOpen(false)}
          >
            Dashboard
          </Link>

          <Link
            to="/pricing"
            className="mobile-item"
            onClick={() => setMenuOpen(false)}
          >
            Pricing
          </Link>

          <Link
            to="/contact"
            className="mobile-item"
            onClick={() => setMenuOpen(false)}
          >
            Contact
          </Link>

          <button
            className="mobile-item nav-button"
            onClick={() => {
              setMenuOpen(false);
              handleCommunityClick();
            }}
          >
            Community <span className="pro-badge">PRO</span>
          </button>

          {user ? (
            <>
              <button
                className="mobile-item"
                onClick={() => {
                  setMenuOpen(false);
                  handleAccountSettings();
                }}
              >
                ⚙️ Account
              </button>

              <button
                className="mobile-item mobile-logout"
                onClick={() => {
                  setMenuOpen(false);
                  handleLogout();
                }}
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/auth"
              className="mobile-item"
              onClick={() => setMenuOpen(false)}
            >
              Login
            </Link>
          )}

        </div>
      )}

    </div>

    {/* Rules Modal */}
    {showRules && (
      <RulesModal
        onAccept={handleRulesAccept}
        onCancel={() => setShowRules(false)}
      />
    )}
  </>
);
}

export default Navbar;

